import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, concat, from, lastValueFrom, mergeMap, Observable, of, tap, toArray } from 'rxjs';
import { Alarm, AlarmLog } from '../class/Alarm';
import { ALARM, ALARMS } from '../component/management-tab/alarms-sub-tab/alarms-routing.constants';
import { AlarmsListService } from './alarms-list.service';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { DialogService } from './dialog.service';
import { EditService } from './edit.service';
import { HelperService } from './helper.service';
import LandmarksService from './landmarks.service';
import { RoutingService } from './routing.service';
import { SnackBarService } from './snack-bar.service';

@Injectable({
  providedIn: 'root',
})
export class AlarmEditService extends BaseService<AlarmLog | number> {
  /**
   * Maximum number of simultaneous `alarm_device` / `alarm_responsable` requests.
   * Browsers only open a handful of connections per origin: firing one request per
   * device at once saturates that pool and pushes every later request to the back of
   * a queue that is silently dropped as soon as the user reloads or leaves the page.
   */
  private static readonly LINK_REQUEST_CONCURRENCY = 4;

  public responsablesChanged = false;
  public devicesChanged = false;
  private editAlarm: Alarm;

  private editService = inject(EditService);
  private _landmarksService = inject(LandmarksService);
  private _authService = inject(AuthService);
  private routingService = inject(RoutingService);
  private helperService = inject(HelperService);
  private router = inject(Router);
  private snackBarService = inject(SnackBarService);
  private dialogService = inject(DialogService);
  private alarmsListService = inject(AlarmsListService);

  constructor() {
    super(inject(ApiService));
  }

  public override HandleResponse(response: Object): number | Object {
    if (response['alarmacquit']) {
      return response['alarmacquit'].data[0].update_count as number;
    } else {
      return response;
    }
  }

  public alarmParams(alarm: Alarm, action: string) {
    const params = {
      action: action,
      uid_owner_responsable: alarm.uid_owner_responsable.toString(),
      name: alarm.name.trim(),
      active: alarm.active,
      type: alarm.type.toString(),
      monday: alarm.monday.toString(),
      tuesday: alarm.tuesday.toString(),
      wednesday: alarm.wednesday.toString(),
      thursday: alarm.thursday.toString(),
      friday: alarm.friday.toString(),
      saturday: alarm.saturday.toString(),
      sunday: alarm.sunday.toString(),
      params: alarm.params ?? '',
      enum_services: alarm.enum_services,
      enum_devices: alarm.enum_devices,
      token: this._authService.$token(),
    };

    if (action === 'update') {
      params['uid'] = alarm.uid.toString();
    }

    for (const key in params) {
      if (params[key] === undefined) delete params[key];
    }
    return params;
  }

  public saveEditAlarm(alarm: Alarm | undefined) {
    this.editAlarm = new Alarm();
    this.editAlarm = this.helperService.cloneDeep(alarm);
  }

  /** Resolves once the alarm and all its links have been saved (or have failed). */
  public save(alarm: Alarm, operation: string): Promise<void> {
    switch (operation) {
      case 'create':
      case 'duplicate':
        return this.addAlarm(alarm);
      case 'edit':
        return this.updateAlarm(alarm);
      default:
        return Promise.resolve();
    }
  }

  /**
   * Creates the alarm, then its responsables and devices links. Resolves once every
   * request has completed, after the outcome has been reported and the user redirected.
   */
  public async addAlarm(alarm: Alarm): Promise<void> {
    const id = await this.requestAlarm(alarm, 'create');
    if (id === -1) {
      this.snackBarService.send({
        message: 'SNACK_BAR.FAILED_OP',
        type: 'error',
      });
      return;
    }

    alarm.uid = id;
    for (const responsable of alarm.responsables) {
      responsable.uid_alarm = id;
    }

    this.alarmsListService.upsertAlarm(alarm);

    // Responsables first: they are few, and they must not wait behind the
    // potentially hundreds of device requests.
    await this.runLinkRequests(
      'SNACK_BAR.CREATED_ALARM',
      this.addResponsables(alarm),
      this.addDevices(alarm)
    );

    this.router.navigate([ALARM(alarm.uid)], { replaceUrl: true });
  }

  /**
   * Updates the alarm, then replaces its responsables and devices links when they
   * changed. Resolves once every request has completed, after the outcome has been
   * reported and the user redirected.
   */
  public async updateAlarm(alarm: Alarm): Promise<void> {
    const id = await this.requestAlarm(alarm, 'update');
    if (id === -1) {
      this.snackBarService.send({
        message: 'SNACK_BAR.FAILED_OP',
        type: 'error',
      });
      this.router.navigate([ALARMS], { skipLocationChange: false });
      return;
    }

    this.updateAlarmPoi(alarm);

    const cloneAlarm = this.helperService.cloneDeep(alarm);

    this.alarmsListService.upsertAlarm(cloneAlarm);

    // Responsables first for the same reason as in addAlarm().
    const linkRequests: Observable<number[]>[] = [];

    if (this.responsablesChanged) {
      linkRequests.push(this.deleteResponsables(alarm).pipe(mergeMap(() => this.addResponsables(alarm))));
      this.responsablesChanged = false;
    }

    if (this.devicesChanged) {
      linkRequests.push(this.deleteDevices(alarm).pipe(mergeMap(() => this.addDevices(alarm))));
      this.devicesChanged = false;
    }

    await this.runLinkRequests('SNACK_BAR.UPDATED_ALARM', ...linkRequests);

    if (alarm.uid) {
      this.router.navigate([ALARM(alarm.uid)], { replaceUrl: true });
    } else {
      this.router.navigate([ALARMS], { skipLocationChange: false });
    }
  }

  /** Sends the `alarm` create/update request; resolves to `-1` when it fails. */
  private async requestAlarm(alarm: Alarm, action: 'create' | 'update'): Promise<number> {
    try {
      return (await lastValueFrom(this.editService.Get('alarm', this.alarmParams(alarm, action)))) as number;
    } catch (error) {
      console.log(error);
      return -1;
    }
  }

  /** Moves the alarm from its previous landmark to its new one (geofencing alarms only). */
  private updateAlarmPoi(alarm: Alarm): void {
    if (alarm.type !== 2) {
      return;
    }

    const landmarksById = this._landmarksService.$landmarksById();
    const previousPoi = landmarksById?.[this.editAlarm.params_alarm.uid_poi];
    const nextPoi = landmarksById?.[alarm.params_alarm.uid_poi];

    if (previousPoi && previousPoi.uid !== nextPoi?.uid) {
      this._landmarksService.setPoiAlarms(
        previousPoi.uid,
        previousPoi.alarms.filter(alarmId => alarmId !== alarm.uid)
      );
    }

    if (nextPoi) {
      const alarms = nextPoi.alarms.filter(alarmId => alarmId !== alarm.uid);
      alarms.push(alarm.uid);
      this._landmarksService.setPoiAlarms(nextPoi.uid, alarms);
    }
  }

  public deleteAlarm(alarm: Alarm) {
    const params = {
      action: 'delete',
      uid: alarm.uid.toString(),
      token: this._authService.$token(),
    };
    let id: number;
    this.dialogService.open({
      title: 'ALARM.ALARMDELETE',
      content: 'COMMON.CONFIRM_DELETE',
      onConfirm: () => {
        this.editService.Get('alarm', params).subscribe(
          response => (id = response),
          error => {
            console.log(error);
          },
          () => {
            if (id != -1) {
              this.snackBarService.send({
                message: 'SNACK_BAR.DELETED_ALARM',
                type: 'success',
              });

              this.alarmsListService.removeAlarms([alarm.uid]);

              if (alarm.type === 2) {
                const poi = this._landmarksService.$landmarksById()?.[alarm.params_alarm.uid_poi];
                if (poi) {
                  this._landmarksService.setPoiAlarms(
                    poi.uid,
                    poi.alarms.filter(alarmId => alarmId !== alarm.uid)
                  );
                }
              }

              concat(this.deleteResponsables(alarm), this.deleteDevices(alarm)).subscribe();

              this.router.navigate([ALARMS]);
            } else {
              this.snackBarService.send({
                message: 'SNACK_BAR.FAILED_OP',
                type: 'error',
              });
            }
          }
        );
      },
    });
  }

  public deleteResponsables(alarm: Alarm): Observable<number> {
    return this.editService
      .Get('alarm_responsable', {
        action: 'delete',
        uid_alarm: alarm.uid.toString(),
        token: this._authService.$token(),
      })
      .pipe(catchError(error => this.handleLinkRequestError(error)));
  }

  public addResponsables(alarm: Alarm): Observable<number[]> {
    return from(alarm.responsables).pipe(
      mergeMap(
        responsable =>
          this.editService
            .Get('alarm_responsable', {
              action: 'create',
              uid_alarm: alarm.uid.toString(),
              uid_responsable: responsable.uid_responsable.toString(),
              sms: responsable.sms,
              mail: responsable.mail,
              gui: responsable.gui,
              acquit: responsable.acquit,
              token: this._authService.$token(),
            })
            .pipe(
              tap(id => {
                if (id !== -1) responsable.uid = id;
              }),
              catchError(error => this.handleLinkRequestError(error))
            ),
        AlarmEditService.LINK_REQUEST_CONCURRENCY
      ),
      toArray()
    );
  }

  public deleteDevices(alarm: Alarm): Observable<number> {
    return this.editService
      .Get('alarm_device', {
        action: 'delete',
        uid_alarm: alarm.uid.toString(),
        token: this._authService.$token(),
      })
      .pipe(catchError(error => this.handleLinkRequestError(error)));
  }

  public addDevices(alarm: Alarm): Observable<number[]> {
    return from(alarm.array_devices).pipe(
      mergeMap(
        uidDevice =>
          this.editService
            .Get('alarm_device', {
              action: 'create',
              uid_alarm: alarm.uid.toString(),
              uid_device: uidDevice.toString(),
              token: this._authService.$token(),
            })
            .pipe(catchError(error => this.handleLinkRequestError(error))),
        AlarmEditService.LINK_REQUEST_CONCURRENCY
      ),
      toArray()
    );
  }

  /**
   * Runs the `alarm_responsable` / `alarm_device` request groups one group after the
   * other, then reports a single outcome: `successMessage` once every request
   * succeeded, or one error if any request of any group failed. The success is only
   * shown at the end so the user never sees "saved" followed by a failure.
   */
  private async runLinkRequests(successMessage: string, ...requests: Observable<number[]>[]): Promise<void> {
    const results = await lastValueFrom(concat(...requests).pipe(toArray()));
    const hasFailed = results.some(ids => ids.includes(-1));
    this.snackBarService.send({
      message: hasFailed ? 'SNACK_BAR.FAILED_OP' : successMessage,
      type: hasFailed ? 'error' : 'success',
    });
  }

  /** Keeps a failed link request from tearing down the whole sequence. */
  private handleLinkRequestError(error: unknown): Observable<number> {
    console.log(error);
    return of(-1);
  }

  public async activeAlarm(alarm: Alarm, active: boolean): Promise<boolean> {
    return new Promise((resolve, reject) => {
      let id: number;
      const params = {
        action: 'update',
        uid: alarm.uid,
        active: active,
        uid_owner_responsable: alarm.uid_owner_responsable,
        token: this._authService.$token(),
      };
      this.editService.Get('alarm', params).subscribe(
        response => (id = response),
        error => {
          console.log(error);
          reject(false);
        },
        () => {
          if (id !== -1) {
            alarm.active = active;
            this.alarmsListService.upsertAlarm(alarm);
            if (active) {
              this.snackBarService.send({
                message: 'SNACK_BAR.ACTIVE_ALARM',
                type: 'success',
              });
            } else {
              this.snackBarService.send({
                message: 'SNACK_BAR.DISABLE_ALARM',
                type: 'success',
              });
            }
            resolve(true);
          } else {
            reject(false);
            this.snackBarService.send({
              message: 'SNACK_BAR.FAILED_OP',
              type: 'error',
            });
          }
        }
      );
    });
  }

  public async acquitLog(uid: number | undefined, uids: number[]) {
    let resp_count: number;
    this.Get('alarm_acquit', {
      token: this._authService.$token(),
      [uid ? 'uid_alarm_log' : 'uid_alarms_log']: uid || uids.join(),
    }).subscribe(
      response => (resp_count = response as number),
      error => {
        console.log(error);
      },
      () => {
        if ((uid && resp_count === 1) || (uids && resp_count === uids.length)) {
          if (this.alarmsListService.$alarmLogs()) {
            this.alarmsListService.$alarmLogs.set(
              this.updateLogsStorage(this.alarmsListService.$alarmLogs(), uid, uids)
            );
            this.alarmsListService.$storedAlarmLogsDates.set({
              start: this.alarmsListService.$storedAlarmLogsDates()?.start,
              end: this.alarmsListService.$storedAlarmLogsDates()?.end,
            });
          } else if (this.alarmsListService.$unacknowledgedAlarm()) {
            this.alarmsListService.$unacknowledgedAlarm.set(
              this.updateLogsStorage(this.alarmsListService.$unacknowledgedAlarm(), uid, uids)
            );
          }
        } else {
          this.snackBarService.send({
            message: 'SNACK_BAR.FAILED_OP',
            type: 'error',
          });
        }
      }
    );
  }

  private updateLogsStorage(data: AlarmLog[], uid: number, uids: number[]) {
    if (uid) {
      data.map(log => {
        if (log.uid === uid) log.acquit = true;
      });
    } else {
      for (const uid of uids) {
        data.map(log => {
          if (log.uid === uids[uid]) log.acquit = true;
        });
      }
    }
    return data;
  }

  public activeOrDeactiveAlarms(alarms: Alarm[], state: 'active_multiple' | 'deactive_multiple') {
    const params = {
      uid_alarms: alarms.map(a => a.uid).join(),
      active: state === 'active_multiple',
      token: this._authService.$token(),
    };

    let resp;
    this.Get('alarms_active', params).subscribe(
      response => (resp = response),
      error => {
        console.log(error);
      },
      () => {
        if (resp?.alarmsactive?.data?.[0]?.update_count) {
          for (const alarm of alarms) {
            alarm.active = params.active;
          }

          this.alarmsListService.upsertAlarms(alarms);

          if (params.active) {
            this.snackBarService.send({
              message: 'SNACK_BAR.ACTIVE_ALARMS',
              type: 'success',
            });
          } else {
            this.snackBarService.send({
              message: 'SNACK_BAR.DISABLE_ALARMS',
              type: 'success',
            });
          }
        } else {
          this.snackBarService.send({
            message: 'SNACK_BAR.FAILED_OP',
            type: 'error',
          });
        }
      }
    );
  }

  public deleteAlarms(alarms: Alarm[]) {
    const params = {
      uid_alarms: alarms.map(a => a.uid).join(),
      token: this._authService.$token(),
    };
    let resp;
    this.dialogService.open({
      title: 'ALARM.ALARMS_DELETE',
      content: 'COMMON.CONFIRM_DELETE',
      onConfirm: () => {
        this.Get('alarms_delete', params).subscribe(
          response => (resp = response),
          error => {
            console.log(error);
          },
          () => {
            if (resp?.alarmsactive?.data?.[0]?.update_count) {
              for (const alarm of alarms) {
                if (alarm.type === 2) {
                  const poi = this._landmarksService.$landmarksById()?.[alarm.params_alarm.uid_poi];
                  if (poi) {
                    this._landmarksService.setPoiAlarms(
                      poi.uid,
                      poi.alarms.filter(alarmId => alarmId !== alarm.uid)
                    );
                  }
                }
              }
              this.alarmsListService.removeAlarms(alarms.map(alarm => alarm.uid));

              this.router.navigate([ALARMS]);
              this.snackBarService.send({
                message: 'SNACK_BAR.DELETED_ALARMS',
                type: 'success',
              });
            } else {
              this.snackBarService.send({
                message: 'SNACK_BAR.FAILED_OP',
                type: 'error',
              });
            }
          }
        );
      },
    });
  }

  public async getConsumedSms() {
    const response = await lastValueFrom(
      this.Get('account_quota', {
        action: 'read',
        token: this._authService.$token(),
      })
    );

    const data = await response['read']['data'];
    return data.length ? Number(data?.[0]?.['consumed']) : 0;
  }
}
